import type { HoleRange } from '@/lib/db/types';

/** Minimum plausible gross for a complete posted round. */
export const MIN_FULL_18_GROSS = 50;
export const MIN_NINE_HOLE_GROSS = 25;

export function expectedHoleCount(holes: HoleRange): number {
  return holes === 'full' ? 18 : 9;
}

export function holeNumsForRange(holes: HoleRange): number[] {
  if (holes === 'front-9') return Array.from({ length: 9 }, (_, i) => i + 1);
  if (holes === 'back-9') return Array.from({ length: 9 }, (_, i) => i + 10);
  return Array.from({ length: 18 }, (_, i) => i + 1);
}

/** True when gross is plausible for a **complete** round of this length. */
export function isValidRoundGross(gross: number, holes: HoleRange): boolean {
  if (!Number.isFinite(gross) || gross < 1) return false;
  if (holes === 'full') return gross >= MIN_FULL_18_GROSS;
  return gross >= MIN_NINE_HOLE_GROSS;
}

export function roundGrossValidationError(holes: HoleRange): string {
  if (holes === 'full') {
    return `Enter a valid 18-hole gross (${MIN_FULL_18_GROSS} or higher).`;
  }
  return `Enter a valid 9-hole gross (${MIN_NINE_HOLE_GROSS} or higher).`;
}

export function validateRoundGross(opts: {
  gross: number;
  holes: HoleRange;
  /** How many holes have a score in this round (for partial Just Play / saves). */
  holesScored?: number;
}): void {
  const { gross, holes, holesScored } = opts;
  if (!Number.isFinite(gross) || gross < 1) {
    throw new Error('Enter a valid gross score.');
  }

  const expected = expectedHoleCount(holes);
  const isPartial = holesScored != null && holesScored > 0 && holesScored < expected;

  if (isPartial) {
    if (gross < holesScored) {
      throw new Error('Total score looks too low for the holes you entered.');
    }
    if (holes === 'full') {
      throw new Error('Finish all 18 holes before posting, or switch to front/back 9 for a partial round.');
    }
    return;
  }

  if (!isValidRoundGross(gross, holes)) {
    throw new Error(roundGrossValidationError(holes));
  }
}

export function grossFromGroupRoundScores(
  scores: Array<{ hole_num: number; strokes: number | null | undefined }>,
  holes: HoleRange,
): { gross: number; hole_scores: Array<{ hole_num: number; strokes: number }> } {
  const allowed = new Set(holeNumsForRange(holes));
  const hole_scores = scores
    .filter((s) => allowed.has(s.hole_num) && typeof s.strokes === 'number' && Number.isFinite(s.strokes))
    .map((s) => ({ hole_num: s.hole_num, strokes: s.strokes as number }));
  const gross = hole_scores.reduce((sum, h) => sum + h.strokes, 0);
  return { gross, hole_scores };
}
